"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { featureFlagService } from "@/services/feature-flags";

interface DemoModeContextType {
  isDemoMode: boolean;
  toggleDemoMode: () => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(
  undefined,
);

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [isDemoMode, setIsDemoMode] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("demoMode");
      return stored ? JSON.parse(stored) : true;
    }
    return true;
  });

  useEffect(() => {
    // Sync demo mode with feature flag service
    featureFlagService.setState({ isDemoMode });

    // Save to localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem("demoMode", JSON.stringify(isDemoMode));
    }
  }, [isDemoMode]);

  const toggleDemoMode = () => {
    setIsDemoMode((prev: boolean) => !prev);
  };

  return (
    <DemoModeContext.Provider value={{ isDemoMode, toggleDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error("useDemoMode must be used within a DemoModeProvider");
  }
  return context;
}
